import React, { useState } from "react";
import { Check, X, ArrowRight } from "lucide-react";

export default function MappingStep({
  sources, connectedIds, mappableFields, targetObjects, mapping, onChange,
  title = "Step 3 — Field Mapping to DMOs",
  description = "Drag a source field onto its matching DMO target field — or tap a source field, then tap the target to map it.",
  targetSectionLabel = "DMO Target Fields",
}) {
  const [selected, setSelected] = useState(null);

  const fieldLabel = (sourceId, key) => {
    const src = sources.find((s) => s.id === sourceId);
    return src?.fields.find((f) => f.key === key)?.label || key;
  };
  const sourceName = (sourceId) => sources.find((s) => s.id === sourceId)?.name || sourceId;

  const assign = (sourceKey, target) => {
    if (!sourceKey) return;
    onChange({ ...mapping, [sourceKey]: target });
    setSelected(null);
  };
  const unassign = (sourceKey) => {
    const next = { ...mapping };
    delete next[sourceKey];
    onChange(next);
  };

  const mappedTargets = new Set(Object.values(mapping));
  const connectedFields = connectedIds.flatMap((sid) => (mappableFields[sid] || []).map((fk) => ({ sourceId: sid, fieldKey: fk })));

  return (
    <div>
      <h3 className="font-heading font-semibold text-white mb-1">{title}</h3>
      <p className="text-sm text-slate-400 mb-4">{description}</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Source Fields</p>
          <div className="space-y-1.5">
            {connectedFields.map(({ sourceId, fieldKey }) => {
              const key = `${sourceId}.${fieldKey}`;
              const mappedTo = mapping[key];
              return (
                <div
                  key={key} draggable data-testid={`mapping-source-field-${key}`}
                  onDragStart={(e) => e.dataTransfer.setData("text/plain", key)}
                  onClick={() => setSelected(selected === key ? null : key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                    selected === key ? "border-indigo-400 bg-indigo-400/10 text-indigo-300" : mappedTo ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-300" : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/25"
                  }`}
                >
                  <span className="font-medium">{sourceName(sourceId)}.{fieldLabel(sourceId, fieldKey)}</span>
                  {mappedTo && (
                    <>
                      <ArrowRight className="w-3 h-3 shrink-0" />
                      <span className="truncate">{mappedTo}</span>
                      <button onClick={(e) => { e.stopPropagation(); unassign(key); }} data-testid={`mapping-unmap-${key}`} className="ml-auto shrink-0 text-slate-400 hover:text-red-400">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{targetSectionLabel}</p>
          <div className="space-y-3">
            {targetObjects.filter((o) => o.fields.length > 0).map((obj) => (
              <div key={obj.object}>
                <p className="text-[11px] font-semibold text-slate-400 mb-1.5">{obj.object}</p>
                <div className="space-y-1.5">
                  {obj.fields.map((f) => {
                    const target = `${obj.object}.${f}`;
                    const filled = mappedTargets.has(target);
                    return (
                      <div
                        key={target} data-testid={`mapping-target-field-${target}`}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => assign(e.dataTransfer.getData("text/plain"), target)}
                        onClick={() => assign(selected, target)}
                        className={`px-3 py-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                          filled ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : selected ? "border-indigo-400/40 border-dashed bg-indigo-400/5 text-slate-300" : "border-white/10 bg-white/[0.02] text-slate-400"
                        }`}
                      >
                        <span className="flex items-center gap-1.5">{filled && <Check className="w-3.5 h-3.5" />} {f}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
